<template>
  <div class="judgment-history">
    <JpIcons />

    <!-- 页面头部：返回 + 标题 -->
    <div class="history-header">
      <div class="history-title-row">
        <img
          class="back-btn"
          src="@/assets/icons/i-return.png"
          alt="返回"
          @click="goBack"
        />
        <div class="history-title">全部裁判文书记录</div>
      </div>
      <div class="history-subtitle">
        查看全部文书生成任务、材料补充记录与生成状态。
      </div>
    </div>

    <div class="history-type-row">
      <TypeTabs v-model="activeHistoryType" />
    </div>

    <!-- 搜索栏 -->
    <div class="card history-toolbar">
      <div class="history-search">
        <svg class="icon"><use href="#jp-search" /></svg>
        <input
          v-model="historyQuery"
          type="text"
          placeholder="搜索案件名称"
        />
      </div>
    </div>

    <!-- 加载中 -->
    <div v-if="historyLoading" class="history-loading">
      <a-spin size="large" />
    </div>

    <!-- 列表（内部滚动）+ 分页器 -->
    <template v-else-if="historyTasks.length">
      <div ref="listScrollRef" class="history-records">
        <TaskRow
          v-for="t in historyTasks"
          :key="t.id"
          :task="t"
          variant="history"
          @view="viewTask"
          @download="downloadTask"
          @delete="deleteTask"
          @missing="infoTask = $event"
        />
      </div>

      <div class="history-pagination">
        <a-pagination
          v-model:current="currentPage"
          v-model:pageSize="pageSize"
          :total="total"
          :show-total="(t) => `共 ${t} 条`"
          show-size-changer
          :page-size-options="['10', '20', '50']"
        />
      </div>
    </template>

    <!-- 空状态 -->
    <div v-else class="history-empty">
      <a-empty />
    </div>

    <!-- 任务进度 / 缺失资料弹窗 -->
    <TaskInfoModal
      :task="infoTask"
      @close="infoTask = null"
      @supplement="goSupplementFromInfo"
    />
    <!-- 补充上传弹窗 -->
    <SupplementModal
      :task="suppTask"
      @close="suppTask = null"
      @confirm="onSuppConfirm"
    />
    <!-- 补充材料流程（useTaskActions 驱动）：解析进度 + 案件信息确认后重新生成 -->
    <ParseProgressModal
      :open="suppParseActive"
      :pct="suppParsePct"
      :desc="suppParseDesc"
    />
    <CaseInfoModal
      :open="suppCaseInfoOpen"
      :config="suppConfig"
      @close="suppCaseInfoOpen = false"
      @submit="submitSuppGenerate"
    />
  </div>
</template>

<script setup>
defineOptions({ name: "JudicativePaperList" });
import { ref, watch, onMounted, onActivated } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import {
  activeHistoryType,
  mapPageItem,
  unwrapPage,
  fetchDocTypes,
  docTypeValue,
} from "./store";
import { getJudgmentPage } from "@/api/judicativePaper";
import { useTaskActions } from "./useTaskActions";
import JpIcons from "./components/JpIcons.vue";
import TypeTabs from "./components/TypeTabs.vue";
import TaskRow from "./components/TaskRow.vue";
import TaskInfoModal from "./components/TaskInfoModal.vue";
import SupplementModal from "./components/SupplementModal.vue";
import ParseProgressModal from "./components/ParseProgressModal.vue";
import CaseInfoModal from "./components/CaseInfoModal.vue";

const router = useRouter();
// useTaskActions 的刷新回调（函数声明有提升，可前置引用）：
// 删除（无参调用）可能删空当前页，先回退一页再拉取；补充材料 / 生成闭环的
// 静默刷新（传 true）行仍在当前页，原地拉取不跳页
const {
  infoTask,
  suppTask,
  suppParseActive,
  suppParsePct,
  suppParseDesc,
  suppCaseInfoOpen,
  suppConfig,
  viewTask,
  downloadTask,
  deleteTask,
  goSupplementFromInfo,
  onSuppConfirm,
  submitSuppGenerate,
} = useTaskActions(refreshAfterDelete);

// ---------- 列表数据（真实接口 GetPage：服务端分页 + Keyword 搜索） ----------
const historyTasks = ref([]);
const historyLoading = ref(false);
const total = ref(0);
const historyQuery = ref("");
const currentPage = ref(1);
const pageSize = ref(10);
const listScrollRef = ref(null);

// 串行守卫：只认最后一次请求的返回（快速翻页 / 连续搜索时丢弃过期响应）
let reqSeq = 0;
async function fetchHistoryPage() {
  const seq = ++reqSeq;
  historyLoading.value = true;
  try {
    const res = await getJudgmentPage({
      pageIndex: currentPage.value,
      pageSize: pageSize.value,
      keyword: historyQuery.value.trim() || undefined,
      // documentType：当前 tab 选中文书类型的枚举 value（activeHistoryType 默认「判决书」，
      // 即默认传判决书对应值；枚举未加载时 docTypeValue 回落类型文案本身）
      documentType: docTypeValue(activeHistoryType.value),
    });
    if (seq !== reqSeq) return;
    const { list, total: t } = unwrapPage(res);
    historyTasks.value = list.map(mapPageItem);
    total.value = t;
  } catch (err) {
    if (seq !== reqSeq) return;
    // 业务 / 状态码错误 http.js 已 toast；此处兜底网络类静默失败
    console.error("[JudicativePaper] getJudgmentPage error:", err);
    message.error("记录列表加载失败，请稍后重试");
  } finally {
    // 过期请求不收 loading：更晚发出的请求仍在飞行中
    if (seq === reqSeq) historyLoading.value = false;
  }
}

// 删除成功后刷新：当前页删到空且不在第一页时先回退一页
// （由下面的 watch 触发拉取，避免请求出一个空页），否则直接刷新当前页；
// silent=true（补充材料 / 生成闭环刷新）跳过回退启发式，原地刷新当前页
function refreshAfterDelete(silent) {
  if (!silent && currentPage.value > 1 && historyTasks.value.length <= 1) {
    currentPage.value -= 1;
  } else {
    fetchHistoryPage();
  }
}

// 翻页 / 改每页条数：v-model 变化后统一拉取，并回到列表顶部
watch([currentPage, pageSize], () => {
  fetchHistoryPage();
  listScrollRef.value?.scrollTo({ top: 0, behavior: "smooth" });
});

// 搜索：防抖 300ms 后回第一页拉取。
// 当前不在第一页时只改页码（由上面的 watch 触发请求），避免重复调用
let searchTimer = null;
watch(historyQuery, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    if (currentPage.value === 1) fetchHistoryPage();
    else currentPage.value = 1;
  }, 300);
});

// tab / 文书类型切换（含 fetchDocTypes 首次回落改写类型的场景）：
// 按新 documentType 回第一页重查；不在第一页时只改页码，由分页 watch 触发请求
watch(activeHistoryType, () => {
  if (currentPage.value === 1) fetchHistoryPage();
  else currentPage.value = 1;
});

// 先等文书类型枚举就绪再拉列表：行内 type 按 documentType 反查文案依赖 docTypeItems
// （fetchDocTypes 失败也会 resolve 并保留默认枚举，不阻断列表加载）
onMounted(() => {
  fetchDocTypes().then(fetchHistoryPage);
});
// 若页面被 keep-alive 缓存：首次激活已随 onMounted 加载，此后每次切回刷新
let activatedOnce = false;
onActivated(() => {
  if (activatedOnce) fetchHistoryPage();
  activatedOnce = true;
});

function goBack() {
  router.push("/home/JudicativePaper");
}
</script>

<style>
/* 记录页显示时，禁止外层 .content-wrapper 自身滚动；
   页面用绝对定位占满 .content（已 position:relative），仅列表内部滚动 */
.content-wrapper:has(.judgment-history) {
  overflow: hidden;
}
</style>

<style scoped>
.judgment-history {
  --brand: #1768ff;
  --ink: #17243a;
  --line: #e5ebf3;
  --line-2: #f0f3f7;
  --shadow: 0 8px 26px rgba(31, 53, 88, 0.06);
  --radius: 16px;
  /* 用绝对定位占满 .content（已 position:relative），整页不滚动，仅列表区域内部滚动 */
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--ink);
}

.icon {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

/* 头部 */
.history-header {
  flex-shrink: 0;
}
.history-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.back-btn {
  width: 36px;
  height: 36px;
  cursor: pointer;
  flex: none;
}
.history-title {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: #17243a;
}
.history-subtitle {
  margin-top: 10px;
  color: #65758e;
  font-size: 15px;
  line-height: 1.7;
}

.history-type-row {
  flex-shrink: 0;
  margin-top: 20px;
}
.history-type-row :deep(.type-tabs) {
  margin-bottom: 16px;
}

/* 搜索栏 */
.history-toolbar {
  flex-shrink: 0;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
}
.history-search {
  position: relative;
  width: min(360px, 100%);
}
.history-search .icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: #8190a5;
  pointer-events: none;
}
.history-search input {
  width: 100%;
  height: 40px;
  border: 1px solid #d8e2ef;
  border-radius: 10px;
  outline: 0;
  padding: 0 13px 0 39px;
  color: #2f405b;
  font-size: 13px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.history-search input::placeholder {
  color: #9aa6b7;
}
.history-search input:focus {
  border-color: #8fb6fb;
  box-shadow: 0 0 0 3px rgba(23, 104, 255, 0.07);
}

/* 列表（内部滚动） */
.history-records {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: grid;
  gap: 12px;
  margin-top: 16px;
  align-content: start;
  padding-right: 4px;
}
.history-records::-webkit-scrollbar {
  width: 6px;
}
.history-records::-webkit-scrollbar-thumb {
  background: rgba(23, 104, 255, 0.18);
  border-radius: 3px;
}
.history-records::-webkit-scrollbar-thumb:hover {
  background: rgba(23, 104, 255, 0.32);
}
.history-records::-webkit-scrollbar-track {
  background: transparent;
}

/* 加载中（撑满剩余空间） */
.history-loading {
  flex: 1;
  min-height: 0;
  display: grid;
  place-items: center;
}

/* 空状态（撑满剩余空间） */
.history-empty {
  flex: 1;
  min-height: 0;
  margin-top: 16px;
  display: grid;
  place-items: center;
  text-align: center;
  color: #7c899d;
  background: #fff;
  border: 1px solid #e5ebf3;
  border-radius: 16px;
  box-shadow: 0 8px 26px rgba(31, 53, 88, 0.06);
}

/* 分页器（固定在页面底部） */
.history-pagination {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 20px;
  padding-bottom: 4px;
}
</style>
